import React, { useContext, useState, useCallback } from 'react';
import { AppHostContext } from '@services/appHost';
import axios from '@helpers/axios';
import * as Sentry from '@sentry/browser';

interface SRPlayerProfile {
  created_at: string;
  partner_user_id: string;
  profile_url: string;
  sportsrecruits_user_id: string;
  total_videos: string;
}

const useSportsRecruits = (claimedPlayerId: string | undefined) => {
  const [linkedPlayerProfile, setLinkedPlayerProfile] =
    useState<SRPlayerProfile>();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = React.useState<string | undefined>();
  const [srUrl, setSrUrl] = useState<string | undefined>(undefined);
  const [toast, setToast] = useState<{
    label: string;
    background: string;
  } | null>(null);

  const appHost = useContext(AppHostContext);
  const appHostService = appHost.isHosted ? appHost.service : undefined;

  const fetchSrLandingPage = useCallback(() => {
    axios
      .get(`/api/sr/player/${claimedPlayerId}/landing_page`)
      .then((response) => {
        setSrUrl(response.data);
        setLoading(false);
      })
      .catch((respError) => {
        if (respError.response && respError.response.status === 404) {
          setLoading(false);
          setError('Player not linked to Sports Recruits');
        } else {
          Sentry.captureException(respError);
          throw respError;
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSRPlayer = useCallback(() => {
    setLoading(true);
    axios
      .get(`/api/sr/player/${claimedPlayerId}`)
      .then((response) => {
        setLinkedPlayerProfile(response.data);
        setLoading(false);
      })
      .catch((respError) => {
        if (respError.response && respError.response.status === 404) {
          setLoading(false);
          setError('Player not linked to Sports Recruits');
        } else {
          Sentry.captureException(respError);
          throw respError;
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const authorizeSRPlayer = useCallback(
    (redirect: string, onClose: () => void) => {
      axios
        .post(`/api/sr/player/${claimedPlayerId}/authorize`)
        .then((response) => {
          setLoading(false);
          if (response.data.isSuccess) {
            if (appHost.isHosted) {
              appHostService?.notifications.openAppLink(redirect);
              onClose();
            } else {
              const newWindow = window.open('', '_blank');
              if (newWindow) {
                newWindow.location = redirect;
              }
              onClose();
            }
          } else {
            setToast({
              label: 'Failed to authorize. Please try again.',
              background: 'status-critical',
            });
          }
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return {
    linkedPlayerProfile,
    srUrl,
    fetchSRPlayer,
    fetchSrLandingPage,
    authorizeSRPlayer,
    toast,
    loading,
    error,
  };
};

export default useSportsRecruits;
